import { Controller } from "@hotwired/stimulus";
import consumer from '../../../javascript/channels/consumer';

declare var Handsontable: any;

// ── Cable payload types ───────────────────────────────────────────────────────

interface ValidationData {
  type: 'validation';
  status: 'start' | 'progress' | 'done' | 'error';
  processed?: number;
  total?: number;
  valid?: boolean;
  error_csv_url?: string;
  voters_validation_id?: number;
}

interface CommitData {
  type: 'commit';
  status: 'commit_start' | 'commit_progress' | 'commit_done' | 'commit_error';
  processed?: number;
  total?: number;
  voters_validation_id: number;
}

interface InvitationsData {
  type: 'invitations';
  status: 'invitations_start' | 'invitations_progress' | 'invitations_done' | 'invitations_error';
  processed?: number;
  total?: number;
  invitations?: number;
  voters_validation_id: number;
}

interface IngestionData {
  type: 'ingestion';
  status: 'done' | 'error';
  voters_validation_id?: number;
  row_count?: number;
}

type CableData = ValidationData | CommitData | InvitationsData | IngestionData;
type StatusKind = 'success' | 'warning' | 'error';

// The validating overlay is cleared by an ActionCable broadcast; the poller is
// the safety net for broadcasts lost to WebSocket drops or subscription races.
const VALIDATION_STATUS_POLL_MS = 5000;

interface ValidationStatusResponse {
  state?: string;
  status?: string;
  errors_count?: number;
  error_csv_url?: string;
}

const HANDSONTABLE_HEADER_HEIGHT = 28;
const HANDSONTABLE_ROW_HEIGHT = 24;
const HANDSONTABLE_MAX_VISIBLE_ROWS = 20;
const HANDSONTABLE_MAX_HEIGHT =
  HANDSONTABLE_HEADER_HEIGHT + (HANDSONTABLE_MAX_VISIBLE_ROWS * HANDSONTABLE_ROW_HEIGHT);

const normalizeRowCount = (rowCount?: number): number =>
  Math.max(0, Math.trunc(rowCount ?? 0));

const handsontableHeight = (rowCount: number): number => {
  const visibleRows = Math.min(rowCount, HANDSONTABLE_MAX_VISIBLE_ROWS);

  return Math.min(
    HANDSONTABLE_HEADER_HEIGHT + (visibleRows * HANDSONTABLE_ROW_HEIGHT),
    HANDSONTABLE_MAX_HEIGHT,
  );
};

interface HotColumn { data: string; }
interface HotConfig {
  columns?: HotColumn[];
  column_headers?: string[];
  remaining_extra_keys?: number;
  last_taken_row?: number;
}

// ── Fetch helpers ─────────────────────────────────────────────────────────────

const csrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? (meta.getAttribute('content') || '') : '';
};

const postJSON = (url: string, payload: unknown): Promise<Record<string, unknown>> =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  }).then(async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return Promise.reject({ status: res.status, body });
    return body as Record<string, unknown>;
  });

// ── Controller ────────────────────────────────────────────────────────────────

export default class extends Controller {
  static targets = [
    'methodSelector',
    'typingPanel',
    'googleSheetsPanel',
    'validateButton',
    'publicUrlInput',
    'overlay',
    'statusMessage',
    'progressBar',
    'progressLabel',
    'confirmButton',
    'downloadErrorsButton',
    'successMessage',
    'resultMessage',
    'mapperPanel',
    'csvUploadPanel',
    'csvFileInput',
  ];

  static values = {
    hotConfig: { type: Object, default: {} },
  };

  // ── Target declarations ──────────────────────────────────────────────────

  declare readonly typingPanelTarget: HTMLElement;
  declare readonly googleSheetsPanelTarget: HTMLElement;
  declare readonly validateButtonTarget: HTMLButtonElement;
  declare readonly publicUrlInputTarget: HTMLInputElement;
  declare readonly overlayTarget: HTMLElement;
  declare readonly statusMessageTarget: HTMLElement;
  declare readonly progressBarTarget: HTMLProgressElement;
  declare readonly progressLabelTarget: HTMLElement;
  declare readonly confirmButtonTarget: HTMLButtonElement;
  declare readonly downloadErrorsButtonTarget: HTMLAnchorElement;
  declare readonly successMessageTarget: HTMLElement;
  declare readonly resultMessageTarget: HTMLElement;
  declare readonly mapperPanelTarget: HTMLElement;
  declare readonly csvUploadPanelTarget: HTMLElement;
  declare readonly csvFileInputTarget: HTMLInputElement;

  declare readonly hasTypingPanelTarget: boolean;
  declare readonly hasGoogleSheetsPanelTarget: boolean;
  declare readonly hasValidateButtonTarget: boolean;
  declare readonly hasPublicUrlInputTarget: boolean;
  declare readonly hasStatusMessageTarget: boolean;
  declare readonly hasProgressBarTarget: boolean;
  declare readonly hasProgressLabelTarget: boolean;
  declare readonly hasDownloadErrorsButtonTarget: boolean;
  declare readonly hasSuccessMessageTarget: boolean;
  declare readonly hasResultMessageTarget: boolean;
  declare readonly hasMapperPanelTarget: boolean;
  declare readonly hasCsvUploadPanelTarget: boolean;
  declare readonly hasCsvFileInputTarget: boolean;
  declare readonly hasConfirmButtonTarget: boolean;
  declare readonly hasOverlayTarget: boolean;

  // ── Value declarations ───────────────────────────────────────────────────

  declare hotConfigValue: HotConfig;

  // ── Private state ────────────────────────────────────────────────────────

  private validationId: number | null = null;
  private voterListUploadId: number | null = null;
  private hotInitialized: boolean = false;
  private validating: boolean = false;
  private channel: { unsubscribe(): void } | null = null;
  private hot: any = null;
  private importMethod: string | null = null;
  private statusPollTimer: number | null = null;
  private statusPollGeneration: number = 0;
  private beforeStreamRenderListener: ((event: Event) => void) | null = null;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  connect(): void {
    this.validationId   = null;
    this.hotInitialized = false;
    this.channel = this.createSubscription();
    this.beforeStreamRenderListener = this.handleBeforeStreamRender.bind(this);
    document.addEventListener('turbo:before-stream-render', this.beforeStreamRenderListener);
    this.resetUI();
    const checked = this.element.querySelector<HTMLInputElement>('input[name="append_import_method"]:checked');
    if (checked) {
      this.importMethod = checked.value;
      this.showMethodPanels(checked.value);
      if (checked.value === 'typing_or_copying') this.initHot();
    } else {
      this.showMethodPanels(null);
    }
  }

  disconnect(): void {
    this.stopStatusPolling();

    if (this.beforeStreamRenderListener) {
      document.removeEventListener('turbo:before-stream-render', this.beforeStreamRenderListener);
      this.beforeStreamRenderListener = null;
    }

    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }

    if (this.hot) {
      this.hot.destroy();
      this.hot = null;
    }

    this.hotInitialized = false;
  }

  // ── Actions wired from the view ──────────────────────────────────────────

  selectMethod(event: Event): void {
    const method = (event.target as HTMLInputElement).value;
    this.importMethod = method;
    this.showMethodPanels(method);
    this.resetUI();
    if (method === 'typing_or_copying') this.initHot();
  }

  validateTypingList(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    if (!this.hot) {
      this.showError('Please wait for the table to finish loading.');
      return;
    }

    const columns = (this.hotConfigValue.columns || []).map((c: HotColumn) => c.data);
    const rawRows: (string | null)[][] = this.hot.getData();
    const rows = rawRows
      .filter((row: (string | null)[]) => row.some((v) => v !== null && v !== undefined && v !== ''))
      .map((row: (string | null)[]) => {
        const obj: Record<string, string | null> = {};
        columns.forEach((col: string, i: number) => {
          const val = row[i];
          obj[col] = (val !== null && val !== undefined) ? String(val) : null;
        });
        return obj;
      });

    if (!rows.length) {
      this.showError('Please enter at least one voter before validating.');
      return;
    }

    if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
    this.submitIngestion({ source: 'inline_rows', source_params: { rows } });
  }

  pickFile(event: CustomEvent<{ fileId: string; accessToken: string }>): void {
    const detail = (event && event.detail) || ({} as { fileId?: string; accessToken?: string });
    if (!detail.fileId || !detail.accessToken) {
      this.showError('Could not read file from Google Picker.');
      return;
    }
    this.submitIngestion({
      source: 'picker',
      source_params: { file_id: detail.fileId, access_token: detail.accessToken },
    });
  }

  handlePickerFilePicked(event: CustomEvent<{ fileId: string; accessToken: string }>): void {
    this.pickFile(event);
  }

  handleUrlSubmitted(event: CustomEvent<{ url: string }>): void {
    const url = event && event.detail && event.detail.url;
    if (!url) {
      this.showError('Please paste a Google Sheets URL.');
      return;
    }
    this.submitIngestion({ source: 'public_url', source_params: { url } });
  }

  submitPublicUrl(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    const url = this.hasPublicUrlInputTarget ? this.publicUrlInputTarget.value.trim() : '';
    if (!url) {
      this.showError('Please paste a Google Sheets URL.');
      return;
    }
    this.submitIngestion({ source: 'public_url', source_params: { url } });
  }

  submitCsvUpload(): void {
    if (!this.hasCsvFileInputTarget) return;
    const file = this.csvFileInputTarget.files?.[0];
    if (!file) {
      this.showError('Please select a CSV file.');
      return;
    }

    const formData = new FormData();
    formData.append('source', 'csv_upload');
    formData.append('file', file);

    this.resetUI();
    this.show(this.overlayTarget);
    this.setStatus('Uploading CSV...');

    const url = this.data.get('validate-url') || '';
    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: formData,
    })
      .then((res) => {
        const contentType = res.headers.get('Content-Type') || '';
        if (contentType.includes('text/vnd.turbo-stream.html')) {
          return res.text().then((html) => {
            const idMatch = html.match(/data-voter-list-upload-id="(\d+)"/);
            if (idMatch) this.voterListUploadId = parseInt(idMatch[1], 10);
            (window as any).Turbo.renderStreamMessage(html);
            this.hide(this.overlayTarget);
            if (this.hasCsvUploadPanelTarget) this.hide(this.csvUploadPanelTarget);
            if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
          });
        }
        return res.json().then((body: Record<string, unknown>) => {
          const msg = (body.error as string) || 'Could not process the CSV file. Please retry.';
          this.showError(msg);
          this.hide(this.overlayTarget);
        });
      })
      .catch(() => {
        this.showError('Could not upload the CSV file. Please retry.');
        this.hide(this.overlayTarget);
      });
  }

  confirmImport(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    if (!this.validationId) {
      this.showError('No validation in progress.');
      return;
    }
    this.disableConfirmButton();
    this.show(this.overlayTarget);
    this.setStatus('Adding voters to your election...');
    postJSON(this.data.get('confirm-url') || '', { voters_validation_id: this.validationId })
      .catch(() => this.showError('Could not start the import. Please retry.'));
  }

  validateIngestionList(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    if (!this.validationId) {
      this.showError('Ingestion state lost. Please start over.');
      return;
    }
    this.validating = true;
    if (this.hasMapperPanelTarget) this.hide(this.mapperPanelTarget);
    this.show(this.overlayTarget);
    this.setStatus('Validating voters...');
    postJSON(this.data.get('start-validation-url') || '', { voters_validation_id: this.validationId })
      .then(() => this.startStatusPolling())
      .catch((err: { body?: { error?: string } }) => {
        this.validating = false;
        this.hide(this.overlayTarget);
        if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
        const msg = (err && (err as any).body && (err as any).body.error) || 'Could not start validation. Please retry.';
        this.showError(msg);
      });
  }

  updateStagingCell(event: Event): void {
    if (this.validating) return;
    const td = event.target as HTMLTableCellElement;
    const attrName = td.getAttribute('data-attr-name') || '';
    if (attrName === 'errors') return;
    const newValue = td.innerText.trim();
    const oldValue = td.getAttribute('data-attr-value') || '';
    if (newValue === oldValue) return;

    const tr = td.closest('tr');
    const vlvId = tr?.getAttribute('data-voter-line-validation-id');
    if (!vlvId || !attrName) return;

    postJSON(
      this.data.get('update-voter-line-url') || '',
      { voter_line_validation_id: parseInt(vlvId, 10), attribute_name: attrName, attribute_value: newValue }
    )
      .then(() => td.setAttribute('data-attr-value', newValue))
      .catch(() => {
        td.innerText = oldValue;
        this.showError('Could not save the change. Please retry.');
      });
  }

  handleColumnMapping(event: Event): void {    const select = event.target as HTMLSelectElement;
    const url = select.getAttribute('data-url');
    if (!url || !this.hasMapperPanelTarget) return;

    const method = (select.getAttribute('data-method') || 'patch').toUpperCase();
    const csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
    const csrfToken = csrfMeta ? csrfMeta.content : '';

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/javascript',
      },
      body: new URLSearchParams({ mapped_column: select.value }).toString(),
      credentials: 'same-origin',
    })
      .then(() => {
        const notices = this.mapperPanelTarget.querySelector<HTMLElement>('#voter_list_notices');
        if (!notices) return;
        const allMapped = Array.from(
          this.mapperPanelTarget.querySelectorAll<HTMLSelectElement>('select[data-url]')
        ).every((s) => s.value !== '');
        if (allMapped) notices.innerHTML = '';
      })
      .catch(() => { /* silent — server validates on confirm */ });
  }

  confirmMapping(event: Event): void {
    if (event && event.preventDefault) event.preventDefault();
    if (!this.voterListUploadId) {
      this.showError('Mapping state lost. Please start over.');
      return;
    }
    if (this.hasMapperPanelTarget) this.hide(this.mapperPanelTarget);
    this.show(this.overlayTarget);
    this.setStatus('Loading voters...');
    postJSON(
      this.data.get('confirm-mapping-url') || '',
      { voter_list_upload_id: this.voterListUploadId }
    )
      .then((response) => {
        this.validationId = response.voters_validation_id as number;
        // ValidationWorker broadcasts will drive the UI from here; the poller
        // is the fallback if any of those broadcasts get lost.
        this.startStatusPolling();
      })
      .catch((err: { body?: { error?: string } }) => {
        this.hide(this.overlayTarget);
        if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
        const msg = (err && (err as any).body && (err as any).body.error) || 'Could not start validation. Please retry.';
        this.showError(msg);
      });
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private submitIngestion(payload: Record<string, unknown>): void {
    this.resetUI();
    this.show(this.overlayTarget);
    this.setStatus('Uploading spreadsheet...');

    const url = this.data.get('validate-url') || '';
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html, application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    })
      .then((res) => {
        const contentType = res.headers.get('Content-Type') || '';
        if (contentType.includes('text/vnd.turbo-stream.html')) {
          // Google Sheets path: Turbo Stream updates the mapper panel
          return res.text().then((html) => {
            // Extract voter_list_upload_id from the meta element in the stream
            // before Turbo processes it (renderStreamMessage is synchronous for
            // the DOM mutation but we read from the raw HTML to be safe).
            const idMatch = html.match(/data-voter-list-upload-id="(\d+)"/);
            if (idMatch) this.voterListUploadId = parseInt(idMatch[1], 10);
            (window as any).Turbo.renderStreamMessage(html);
            this.hide(this.overlayTarget);
            this.hide(this.googleSheetsPanelTarget);
            if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
          });
        }
        // JSON path (inline rows or error)
        return res.json().then((body: Record<string, unknown>) => {
          if (!res.ok) {
            const msg = (body.error as string) || 'Could not upload the spreadsheet. Please retry.';
            this.showError(msg);
            this.hide(this.overlayTarget);
            return;
          }
          // Inline rows are already chained to ValidationWorker server-side.
          // Track that job here instead of enqueueing the same validation again.
          this.validationId = body.voters_validation_id as number;
          this.validating = true;
          if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
          this.setStatus('Validating voters...');
          this.startStatusPolling();
        });
      })
      .catch(() => {
        this.showError('Could not upload the spreadsheet. Please retry.');
        this.hide(this.overlayTarget);
      });
  }

  private createSubscription(): { unsubscribe(): void } {
    return consumer.subscriptions.create(
      {
        channel: 'AppendVotersChannel',
        user_id: this.data.get('user-id'),
      },
      {
        // A (re)connect may have missed broadcasts — reconcile immediately.
        connected: () => this.reconcileValidationStatus(),
        disconnected: () => {},
        received: this.cableReceived.bind(this),
      }
    );
  }

  private cableReceived(data: CableData): void {
    switch (data.type) {
      case 'ingestion': this.handleIngestion(data); break;
      case 'validation': this.handleValidation(data); break;
      case 'commit': this.handleCommit(data); break;
      case 'invitations': this.handleInvitations(data); break;
    }
  }

  private handleValidation(data: ValidationData, verifiedTerminal: boolean = false): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    switch (data.status) {
      case 'start':
        this.validating = true;
        if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
        if (this.hasOverlayTarget) this.show(this.overlayTarget);
        this.setStatus('Validating voters...');
        this.updateProgress(0, 0);
        this.startStatusPolling();
        break;
      case 'progress':
        this.updateProgress(data.processed || 0, data.total || 0);
        break;
      case 'done':
        if (!verifiedTerminal) {
          this.reconcileValidationStatus();
          break;
        }
        this.validating = false;
        this.stopStatusPolling();
        if (data.valid) {
          this.enableConfirmButton();
          if (this.hasOverlayTarget) this.hide(this.overlayTarget);
          if (this.hasMapperPanelTarget && this.importMethod !== 'typing_or_copying') {
            this.show(this.mapperPanelTarget);
          }
          this.setResult('Your voter list is valid. Click Add Voters to continue.', 'success');
        } else {
          if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
          if (this.hasOverlayTarget) this.hide(this.overlayTarget);
          this.setResult(
            'There are errors in your list that must be fixed before continuing. Select Download List to view the error and explanation in a spreadsheet. Then, fix the error, and verify by selecting Validate list again.',
            'error'
          );
          if (this.hasDownloadErrorsButtonTarget && data.error_csv_url) {
            this.downloadErrorsButtonTarget.setAttribute('href', data.error_csv_url);
            this.show(this.downloadErrorsButtonTarget);
          }
          if (this.hasMapperPanelTarget && this.importMethod !== 'typing_or_copying') {
            this.show(this.mapperPanelTarget);
          } else {
            // typing_or_copying or no mapper panel: keep confirm button disabled.
            this.disableConfirmButton();
          }
        }
        break;
      case 'error':
        if (!verifiedTerminal) {
          this.reconcileValidationStatus();
          break;
        }
        this.validating = false;
        this.stopStatusPolling();
        if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        this.showError('Validation failed. Please retry.');
        if (this.hasMapperPanelTarget) this.show(this.mapperPanelTarget);
        break;
    }
  }

  private handleIngestion(data: IngestionData): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    if (data.status !== 'done') {
      if (this.hasOverlayTarget) this.hide(this.overlayTarget);
      this.showError('Ingestion failed. Please start over.');
      return;
    }

    this.validationId = data.voters_validation_id as number;
    // Validation starts automatically server-side; keep the overlay visible
    // and update the status — handleValidation will manage transitions from here.
    this.setStatus('Validating voters...');
    this.startStatusPolling();
  }

  // The append_voters stream is per-USER: every attempt (each Validate click
  // creates a new VotersValidation with its own jobs) broadcasts into it, and
  // stale jobs from earlier attempts can fire minutes later. Only messages for
  // the attempt this page is tracking may drive the UI.
  private staleAttempt(votersValidationId?: number): boolean {
    return !votersValidationId ||
      !this.validationId ||
      votersValidationId !== this.validationId;
  }

  // ── Validation status reconciliation ─────────────────────────────────────
  //
  // The overlay is normally cleared by an ActionCable broadcast, but a broadcast
  // can be lost (WebSocket drop, broadcast racing the subscription, worker
  // no-op). While a validation is pending we poll the status endpoint and feed
  // any terminal state through the same handler the cable path uses.

  private startStatusPolling(): void {
    this.stopStatusPolling();
    this.statusPollGeneration += 1;
    this.statusPollTimer = window.setInterval(
      () => this.reconcileValidationStatus(),
      VALIDATION_STATUS_POLL_MS,
    );
  }

  private stopStatusPolling(): void {
    if (this.statusPollTimer !== null) {
      window.clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
  }

  private reconcileValidationStatus(): void {
    const url = this.data.get('validation-status-url') || '';
    const validationId = this.validationId;
    const pollGeneration = this.statusPollGeneration;
    if (!url || !validationId || this.statusPollTimer === null) return;

    fetch(`${url}?voters_validation_id=${validationId}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: ValidationStatusResponse | null) => {
        if (
          !body ||
          this.statusPollTimer === null ||
          validationId !== this.validationId ||
          pollGeneration !== this.statusPollGeneration
        ) return;

        if (body.state === 'completed' && (body.status === 'valid' || body.status === 'invalid')) {
          this.handleValidation({
            type: 'validation',
            status: 'done',
            valid: body.status === 'valid',
            error_csv_url: body.error_csv_url,
            voters_validation_id: validationId,
          }, true);
        } else if (body.state === 'failed') {
          this.handleValidation({
            type: 'validation',
            status: 'error',
            voters_validation_id: validationId,
          }, true);
        }
        // created/running: work is still in flight — keep waiting.
      })
      .catch(() => { /* transient — the next poll retries */ });
  }


  private handleCommit(data: CommitData): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    switch (data.status) {
      case 'commit_start':
        if (this.hasOverlayTarget) this.show(this.overlayTarget);
        this.setStatus('Adding voters to your election...');
        break;
      case 'commit_progress':
        this.updateProgress(data.processed || 0, data.total || 0);
        this.setStatus(`Importing row ${data.processed} of ${data.total}...`);
        break;
      case 'commit_done':
        this.setStatus('Voters successfully added! Sending invitations now...');
        break;
      case 'commit_error':
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        this.setResult('Import failed. Please contact support.', 'error');
        break;
    }
  }

  private handleInvitations(data: InvitationsData): void {
    if (this.staleAttempt(data.voters_validation_id)) return;

    switch (data.status) {
      case 'invitations_start':
        this.setStatus('Preparing invitations...');
        break;
      case 'invitations_progress':
        this.updateProgress(data.processed || 0, data.total || 0);
        break;
      case 'invitations_done':
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        if (this.hasSuccessMessageTarget) {
          this.successMessageTarget.textContent =
            `Voters successfully added! ${data.invitations} invitations queued.`;
          this.show(this.successMessageTarget);
        }
        this.setResult('Voters successfully added!', 'success');
        window.location.href = this.data.get('success-url') || '';
        break;
      case 'invitations_error':
        if (this.hasOverlayTarget) this.hide(this.overlayTarget);
        this.setResult('Invitations could not be created. Please contact support.', 'error');
        break;
    }
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  private handleBeforeStreamRender(event: Event): void {
    const stream = event.target as HTMLElement | null;
    if (!stream || stream.getAttribute('target') !== 'append_voters_mapper_panel') return;

    const template = stream.querySelector('template') as HTMLTemplateElement | null;
    const incomingOverview = template?.content.querySelector<HTMLElement>('[data-voters-validation-id]');
    if (!incomingOverview) return;

    const incomingValidationId = Number(incomingOverview.dataset.votersValidationId);
    if (!this.validationId || incomingValidationId !== this.validationId) event.preventDefault();
  }

  private initHot(): void {
    if (this.hotInitialized) return;
    const cfg = this.hotConfigValue;
    if (!cfg || !cfg.columns || !cfg.columns.length) return;

    const container = document.getElementById('hands_on_table');
    if (!container) return;

    if (typeof Handsontable === 'undefined') {
      console.error('[AppendVotersController] Handsontable not loaded');
      return;
    }

    const maxRows = normalizeRowCount(cfg.remaining_extra_keys);

    this.hot = new Handsontable(container, {
      columns: cfg.columns,
      colHeaders: cfg.column_headers,
      rowHeaders: (index: number) => String(index + 1 + (cfg.last_taken_row || 0)),
      minSpareRows: maxRows,
      maxRows,
      dataSchema: cfg.columns.reduce((memo: Record<string, null>, col: HotColumn) => {
        memo[col.data] = null;
        return memo;
      }, {}),
      autoWrapRow: true,
      autoWrapCol: true,
      stretchH: 'all',
      width: '100%',
      height: handsontableHeight(maxRows),
      rowHeights: HANDSONTABLE_ROW_HEIGHT,
      pasteMode: 'overwrite',
      preventOverflow: 'horizontal',
      contextMenu: false,
    });

    this.hotInitialized = true;
  }

  private showMethodPanels(method: string | null): void {
    if (this.hasTypingPanelTarget) {
      this.typingPanelTarget.classList.toggle('hide', method !== 'typing_or_copying');
    }
    if (this.hasGoogleSheetsPanelTarget) {
      this.googleSheetsPanelTarget.classList.toggle('hide', method !== 'google_sheets');
    }
    if (this.hasCsvUploadPanelTarget) {
      this.csvUploadPanelTarget.classList.toggle('hide', method !== 'csv_upload');
    }
  }

  startOver(): void {
    this.resetUI();
    const checked = this.element.querySelector<HTMLInputElement>('input[name="append_import_method"]:checked');
    if (checked) this.showMethodPanels(checked.value);
  }

  private resetUI(): void {
    this.stopStatusPolling();
    this.validationId = null;
    this.validating = false;
    this.disableConfirmButton();
    if (this.hasDownloadErrorsButtonTarget) this.hide(this.downloadErrorsButtonTarget);
    if (this.hasSuccessMessageTarget) this.hide(this.successMessageTarget);
    if (this.hasOverlayTarget) this.hide(this.overlayTarget);
    if (this.hasResultMessageTarget) this.hide(this.resultMessageTarget);
    if (this.hasMapperPanelTarget) {
      this.hide(this.mapperPanelTarget);
      this.mapperPanelTarget.innerHTML = '';
    }
    this.voterListUploadId = null;
    if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
    this.setStatus('');
    this.updateProgress(0, 0);
  }

  private setStatus(text: string, kind?: StatusKind): void {
    if (!this.hasStatusMessageTarget) return;
    this.statusMessageTarget.textContent = text || '';
    this.statusMessageTarget.classList.remove('inline_success', 'inline_error', 'inline_warning');
    if (kind === 'success') this.statusMessageTarget.classList.add('inline_success');
    if (kind === 'warning') this.statusMessageTarget.classList.add('inline_warning');
    if (kind === 'error') this.statusMessageTarget.classList.add('inline_error');
  }

  private showError(text: string): void {
    this.setResult(text, 'error');
  }

  private setResult(text: string, kind?: StatusKind): void {
    if (!this.hasResultMessageTarget) return;
    this.resultMessageTarget.textContent = text || '';
    this.resultMessageTarget.classList.remove('inline_success', 'inline_error', 'inline_warning');
    if (kind === 'success') this.resultMessageTarget.classList.add('inline_success');
    if (kind === 'warning') this.resultMessageTarget.classList.add('inline_warning');
    if (kind === 'error') this.resultMessageTarget.classList.add('inline_error');
    if (text) this.show(this.resultMessageTarget);
  }

  private updateProgress(processed: number, total: number): void {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.value = processed || 0;
      this.progressBarTarget.max   = total || 0;
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = total ? `${processed} / ${total}` : '';
    }
  }

  private enableConfirmButton(): void {
    if (!this.hasConfirmButtonTarget) return;
    this.confirmButtonTarget.disabled = false;
    this.confirmButtonTarget.classList.remove('toolkit-button-component--disabled');
  }

  private disableConfirmButton(): void {
    if (!this.hasConfirmButtonTarget) return;
    this.confirmButtonTarget.disabled = true;
    this.confirmButtonTarget.classList.add('toolkit-button-component--disabled');
  }

  private show(el: HTMLElement | null): void {
    if (el && el.classList) el.classList.remove('hide');
  }

  private hide(el: HTMLElement | null): void {
    if (el && el.classList) el.classList.add('hide');
  }
}
